import {IGameInfo, IUser} from '@/entities/user/types';
import {IGame, IShoot} from '@/entities/game/types';
import {ILucky} from '@/entities/lucky/types';
import {ITeam} from '@/entities/team/types';
import axios from 'axios';
import {encryptJson} from '../utils/encryptJson';
import {IBet, IMatch, ISetBet} from '@/entities/forecast/types';
import {Inventory, InventoryLimit} from "@/entities/inventory/types";
import {IQuestCheck, Quest} from "@/entities/quest/types";
import {IRatingList, IRatingTabs} from "@/entities/rating/types";

//const baseURL = 'https://dev-ovi.alltooptimization.ru'
const baseURL = 'https://1339-test.alltooptimization.ru'
const version = 'v2'

export const TG = window.Telegram?.WebApp;
const initData = TG.initData || '';
const initDataBase64 = btoa(initData);


const api = axios.create({
    baseURL: baseURL + '/api/' + version,
    timeout: 20000,
    headers: {
        'Authorization': 'Bearer ' + initDataBase64,
        'Content-Type': 'application/json'
    }
});

api.interceptors.response.use(
    (response) => {
        // Проверяем бизнес-ошибки
        if (response.data?.status === false) {
            return Promise.reject(new Error(response.data.description));
        }
        return response;
    },
    (error) => {
        // если отменили запрос или истекло время ожидания ответа
        if (
            axios.isCancel(error) ||
            error.code === "ERR_CANCELED" ||
            error.code === "ECONNABORTED"
        ) {
            return Promise.reject(new Error("Истекло время ожидания ответа от сервера"));
        }
        // HTTP/сетевые ошибки
        if (!error.response) {
            return Promise.reject(new Error("Нет соединения с сервером"));
        }

        //Ошибка сервера
        const status = error.response.status;
        if (status >= 500) {
            return Promise.reject(new Error("Ошибка на сервере, попробуйте позже"));
        }
        return Promise.reject(error);
    }
);

type FetchResult<T> =
    | { success: true; data: T }
    | { success: false; error: string };


function toFetchError<T = unknown>(error: unknown): FetchResult<T> {
    if (typeof error === 'string') {
        return {success: false, error};
    }
    if (error instanceof Error) {
        return {success: false, error: `${error.name}: ${error.message}`};
    }
    return {success: false, error: 'Неизвестная ошибка'};
}


export const fetchProfile = async (): Promise<FetchResult<IUser>> => {
    try {
        const response = await api.get<IUser>('/users/profile');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchRatingTabs = async (): Promise<FetchResult<IRatingTabs[]>> => {
    try {
        const response = await api.get<IRatingTabs[]>('/rating/list');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchRating = async (id:string): Promise<FetchResult<IRatingList[]>> => {
    try {
        const response = await api.get<IRatingList[]>( `/rating/${id}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchReferralLink = async (): Promise<FetchResult<string>> => {
    try {
        const response = await api.get<string>('/users/referral-link');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchReferralWheelLink = async (): Promise<FetchResult<string>> => {
    try {
        const response = await api.get<string>('/users/referral-wheel-link');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchGameInfo = async (): Promise<FetchResult<IGameInfo>> => {
    try {
        const response = await api.get<IGameInfo>('/users/game-info');
        //const response = await apiMock.get<IGameInfo>('/game-info');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};


export const executeKick = async (gameData: IGame): Promise<FetchResult<IShoot>> => {
    try {
        const payload = encryptJson(gameData);
        const response = await api.put<IShoot>('/game/shoot', {data: payload});
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchLuckyWheel = async (): Promise<FetchResult<ILucky>> => {
    try {
        const response = await api.get('/wheel/spin');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchFriends = async (): Promise<FetchResult<number>> => {
    try {
        const response = await api.get('/users/friends');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchTeams = async (): Promise<FetchResult<ITeam[]>> => {
    try {
        const response = await api.get<ITeam[]>('/team');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchMyTeam = async (): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.get<ITeam>('/team/my');
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchTeam = async (id: string): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.get<ITeam>(`/team/${id}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchInviteLink = async (): Promise<FetchResult<string>> => {
    try {
        const response = await api.get<string>(`/team/link`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const searchTeams = async (name: string): Promise<FetchResult<ITeam[]>> => {
    try {
        const response = await api.get<ITeam[]>(`/team/search?name=${name}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const createTeam = async (name: string, file: FormData): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.post<ITeam>(
            `/team?name=${name}`,
            file,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const editTeam = async (name?: string, file?: FormData): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.put<ITeam>(
            `/team?name=${name}`,
            file,
            {headers: {'Content-Type': 'multipart/form-data'}}
        );
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const delTeam = async (): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.delete<ITeam>(`/team`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const joinTeam = async (inviteCode: string): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.post<ITeam>(`/team/join?inviteCode=${inviteCode}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const leaveTeam = async (userId: number | null): Promise<FetchResult<ITeam>> => {
    try {
        const response = await api.post<ITeam>(`/team/leave${userId !== null ? '?userId=' + userId : ''}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};


export const fetchMatches = async (): Promise<FetchResult<IMatch[]>> => {
    try {
        const response = await api.get<IMatch[]>(`/matches`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchMatch = async (id: string): Promise<FetchResult<IMatch>> => {
    try {
        const response = await api.get<IMatch>(`/matches/${id}`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const setBet = async (id: number, bet: ISetBet): Promise<FetchResult<IBet>> => {
    try {
        const response = await api.post<IBet>(`/matches/${id}/bet`, bet);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};

export const fetchBets = async (id: number): Promise<FetchResult<IBet[]>> => {
    try {
        const response = await api.get<IBet[]>(`/matches/${id}/bets`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};


export const fetchInventory = async (): Promise<FetchResult<Inventory>> => {
    try {
        const response = await api.get<Inventory>(`/inventory/items`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const fetchInventoryLimits = async (): Promise<FetchResult<InventoryLimit>> => {
    try {
        const response = await api.get<InventoryLimit>(`/inventory/limits`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const buyInventoryItem = async (itemId: string, level: number, email: string): Promise<FetchResult<{paymentUrl:string}>> => {
    try {
        const response = await api.post<{paymentUrl:string}>(`/payments/create-inventory-payment`, {itemId,level,email});
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};


export const fetchQuests = async (): Promise<FetchResult<Quest>> => {
    try {
        const response = await api.get<Quest>(`/quests`);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
export const checkQuest = async (payload:IQuestCheck): Promise<FetchResult<IQuestCheck>> => {
    try {
        const response = await api.post<IQuestCheck>(`/quests`, payload);
        return {success: true, data: response.data};
    } catch (error) {
        return toFetchError(error)
    }
};
